/**
 * The first-run Link-in-Bio wizard, as data.
 *
 * Kept identical to the mobile copy at
 * modules/influencer/components/zap/link-in-bio/bioSetup.ts — only the import
 * line and this comment differ. Change one, copy it across.
 *
 * Same story as storefrontSetup.ts: the old setup screen showed a profile
 * form with an avatar dropbox, a name, a handle, a bio, a social-links
 * repeater and a theme picker all at once — and greeted a brand-new creator
 * with "Profile name cannot be blank" in red before they had typed anything.
 * POST /bio/setup only requires a slug; display_name, headline, avatar_url,
 * social_links and theme all default server-side. So the page can exist after
 * step 1, and everything else is something they choose to add.
 */

import { BIO_THEMES } from '../../../../core/services/bio.service';

export interface BioSocialLink {
  platform: string;
  url: string;
}

export interface BioThemeValue {
  bg: string;
  accent: string;
  text: string;
  buttonText: string;
  button_shape: string;
}

/** Every field POST /bio/setup accepts, minus the ids. */
export interface BioDraft {
  slug: string;
  display_name: string;
  headline: string;
  avatar_url: string;
  avatar_file_name: string;
  avatar_file_type: string;
  avatar_file_path: string;
  avatar_storage_size: string | number;
  social_links: BioSocialLink[];
  theme: BioThemeValue;
}

export type BioStepId = 'name' | 'look' | 'about' | 'review';

export interface BioStep {
  id: BioStepId;
  /** Shown in the progress line — one word, like the other Zap wizards. */
  stepLabel: string;
  title: string;
  subtitle: string;
  /** Skippable steps show "Skip" instead of "Next". */
  optional: boolean;
}

export const BIO_STEPS: BioStep[] = [
  {
    id: 'name',
    stepLabel: 'Name',
    title: 'What should your page be called?',
    subtitle: 'This is the name at the top of your page. You can change it any time.',
    optional: false,
  },
  {
    id: 'look',
    stepLabel: 'Look',
    title: 'Add a photo and pick a colour',
    subtitle: 'Your face and your colour are what make the page yours. Skip if you would rather do it later.',
    optional: true,
  },
  {
    id: 'about',
    stepLabel: 'About',
    title: 'Say who you are',
    subtitle: 'One line under your name, plus wherever else people can find you.',
    optional: true,
  },
  {
    id: 'review',
    stepLabel: 'Done',
    title: 'Here is your page',
    subtitle: 'This is exactly what people will see. Happy with it? Go live.',
    optional: false,
  },
];

export const slugify = (s: string): string =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/** Title-cases a profile name that was typed all-lowercase. */
function tidyName(raw: string): string {
  return raw
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** A starter line for the About step, so the box is never an empty stare. */
export function suggestHeadline(displayName?: string): string {
  const first = (displayName || '').trim().split(/\s+/)[0];
  const who = first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
  return who
    ? `Hi, I'm ${who}. Everything I'm sharing right now lives here.`
    : 'Everything I am sharing right now lives here.';
}

export function emptyBioDraft(opts?: { displayName?: string }): BioDraft {
  return {
    slug: slugify(opts?.displayName || ''),
    display_name: tidyName(opts?.displayName || ''),
    headline: '',
    avatar_url: '',
    avatar_file_name: '',
    avatar_file_type: '',
    avatar_file_path: '',
    avatar_storage_size: '',
    social_links: [],
    theme: { ...BIO_THEMES.light, button_shape: 'rounded' },
  };
}

/**
 * What blocks Next on a given step, or null when it may proceed.
 * Only the name step can block — that is the whole point of the rewrite.
 */
export function bioStepError(
  stepId: BioStepId,
  draft: BioDraft,
  slugAvailable: boolean | null
): string | null {
  if (stepId === 'name') {
    if (!draft.display_name.trim()) return 'Your page needs a name.';
    if (!draft.slug.trim()) return 'Your page needs a link.';
    if (draft.slug.trim().length < 3) return 'Your link needs at least 3 characters.';
    if (slugAvailable === false) return 'That link is taken — try another or tap the refresh icon.';
    return null;
  }
  if (stepId === 'about') {
    // A row with a platform but no address would save as a dead link.
    const broken = draft.social_links.find((l) => l.platform.trim() && !l.url.trim());
    if (broken) return `Add the address for ${broken.platform.trim()}, or remove that row.`;
  }
  return null;
}

export function canFinishBio(draft: BioDraft, slugAvailable: boolean | null): boolean {
  return (
    bioStepError('name', draft, slugAvailable) === null &&
    bioStepError('about', draft, slugAvailable) === null
  );
}

/** A random-suffixed handle, for when the obvious one is taken. */
export function regeneratedBioSlug(base: string): string {
  const root = slugify(base) || 'me';
  return `${root}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Social rows worth saving — a half-filled row is dropped, not persisted. */
export function cleanSocialLinks(links: BioSocialLink[]): BioSocialLink[] {
  return links.filter((l) => l.platform.trim() && l.url.trim());
}

/**
 * What the review step lists back. Skipped items are named as skipped rather
 * than hidden, so nobody finishes wondering whether they missed something.
 */
export function bioReviewRows(draft: BioDraft): { label: string; value: string; missing: boolean }[] {
  const socials = cleanSocialLinks(draft.social_links).length;
  return [
    { label: 'Page name', value: draft.display_name || 'Not set', missing: !draft.display_name },
    { label: 'Photo', value: draft.avatar_url ? 'Added' : 'Add later', missing: !draft.avatar_url },
    { label: 'About line', value: draft.headline ? 'Added' : 'Add later', missing: !draft.headline },
    {
      label: 'Social links',
      value: socials ? `${socials} added` : 'Add later',
      missing: socials === 0,
    },
  ];
}

/** Whether an optional step has anything in it — decides Skip vs Next. */
export function bioStepIsFilled(id: BioStepId, draft: BioDraft, defaultThemeBg: string): boolean {
  if (id === 'look') return !!draft.avatar_url || draft.theme.bg !== defaultThemeBg;
  if (id === 'about') return !!draft.headline || cleanSocialLinks(draft.social_links).length > 0;
  return true;
}
